import { ArrowDownOutlined, ArrowUpOutlined, MinusOutlined } from "@ant-design/icons";
import { isArray, isNumber, isUndefined, mapValues, omitBy } from "lodash";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { clearAsyncDataCache } from "./hooks";
import { delLocalStorage, LOCAL_STORAGE_TYPE } from "./localStorage";

export function percentComponent(percent) {
  if (parseInt(percent) !== 0 && !percent) return null;
  if (parseInt(percent) > 0)
    return (
      <span className="text-success mr-2 flex items-center">
        <ArrowUpOutlined className="mt-[1px]" />
        {Math.abs(percent.toFixed(2))}%
      </span>
    );
  if (parseInt(percent) < 0)
    return (
      <span className="text-danger mr-2 flex items-center">
        <ArrowDownOutlined className="mt-[1px]" />
        {Math.abs(percent.toFixed(2))}%
      </span>
    );
  return (
    <span className="text-warning mr-2 flex items-center">
      <MinusOutlined className="mt-[1px]" />
      {Math.abs(percent.toFixed(2))}%
    </span>
  );
}

export function getMenuItem(label, key, icon, children, type) {
  return {
    key,
    icon,
    children,
    label,
    type,
  };
}

export function convertToShortScale(number, toFixedNumber) {
  if (number >= 1000) {
    const suffixes = ["", "K", "M", "B", "T"];
    const suffixIndex = Math.floor(Math.log10(number) / 3);
    const shortScaleNum = (number / Math.pow(1000, suffixIndex)).toFixed(1);
    return shortScaleNum + suffixes[suffixIndex];
  } else {
    return Number.isInteger(number) ? number.toString() : number.toFixed(toFixedNumber ?? 1);
  }
}

export function getConditionString(data) {
  switch (data.condition) {
    case "gt":
      return `${data.key} > ${data.value}`;
    case "gte":
      return `${data.key} >= ${data.value}`;
    case "lt":
      return `${data.key} < ${data.value}`;
    case "lte":
      return `${data.key} <= ${data.value}`;
    case "eq":
      return `${data.key} = ${data.value}`;
    case "between":
      return `${data.value[0]} <= ${data.key} <= ${data.value[1]}`;
    case "in":
      return `${data.key} in ${data.value}`;
    case "email":
      return `${data.key} is email`;
    case "regex":
      return `${data.key} of ${data.value}`;
    case "fired":
      return `${data.key} fired ${data.value}`;
    default:
      return "";
  }
}

export function getShortName(fullname) {
  if (!fullname) return "NA";
  const parts = fullname.split(" ");
  if (parts.length < 2) return fullname.substr(0, 2);
  return `${parts[0].substr(0, 1)}${parts[0].substr(0, 1)}`;
}

export function addUniqueKeyForTableData(array) {
  return array.map((item) =>
    omitBy(
      {
        key: uuidv4(),
        ...item,
        children: item.children?.length ? addUniqueKeyForTableData(item.children) : undefined,
      },
      isUndefined
    )
  );
}

export function findChildren(key, store) {
  let result;
  const parent = store.find((item) => item.key === key);
  if (parent) {
    result = parent.children;
  } else {
    store.forEach((item) => {
      if (!result) {
        result = findChildren(key, item.children || []);
      }
    });
  }
  return result;
}

export const REPEAT = {
  NONE: "NONE",
  REPEAT: "REPEAT",
};

export const LIST_REPEAT = [
  {
    label: "Dont' repeat",
    value: "NONE",
  },
  {
    label: "Repeat",
    value: "REPEAT",
  },
];

export const FREQUENCY_TYPE = {
  DAY: "DAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
  YEAR: "YEAR",
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  MONTHLY: "MONTHLY",
  YEARLY: "YEARLY",
};

export const LIST_FREQUENCY_TYPE = [
  {
    label: "Day",
    value: "DAY",
  },
  {
    label: "Week",
    value: "WEEK",
  },
  {
    label: "Month",
    value: "MONTH",
  },
  {
    label: "Year",
    value: "YEAR",
  },
];

export const DAY_OF_WEEK = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export const DURATIONS = [
  {
    value: "seconds",
    label: "Seconds",
  },
  {
    value: "minutes",
    label: "Minutes",
  },
  {
    value: "hours",
    label: "Hours",
  },
  {
    value: "days",
    label: "Days",
  },
];

export function getShortOccursText(data) {
  switch (data.repeatType) {
    case REPEAT.NONE:
      return `At ${data.repeatValue}`;
    case FREQUENCY_TYPE.DAILY:
    case FREQUENCY_TYPE.DAY:
      return `Repeat daily at ${moment(data.repeatValue).format("HH:mm")}`;
    case FREQUENCY_TYPE.WEEK:
    case FREQUENCY_TYPE.WEEKLY:
      return `Repeat weekly from ${data.repeatValue}`;
    case FREQUENCY_TYPE.MONTH:
    case FREQUENCY_TYPE.MONTHLY:
      return `Repeat monthly from ${data.repeatValue}`;
    case FREQUENCY_TYPE.YEAR:
    case FREQUENCY_TYPE.YEARLY:
      return `Repeat yearly from ${data.repeatValue}`;
    default:
      return "";
  }
}

export function getOccursText(data) {
  const prefix = "Occurs ";
  let resultText = "";
  switch (data.repeatType) {
    case REPEAT.NONE:
      resultText = `${prefix} at ${data.repeatValue}`;
      break;
    case FREQUENCY_TYPE.DAY:
    case FREQUENCY_TYPE.DAILY:
      resultText =
        +data.repeatInterval === 1
          ? `${prefix} every day from ${data.repeatValue}`
          : `${prefix} other day from ${data.repeatValue}`;
      break;
    case FREQUENCY_TYPE.WEEK:
    case FREQUENCY_TYPE.WEEKLY:
      resultText =
        +data.repeatInterval === 1
          ? `${prefix} every week from ${data.repeatValue}`
          : `${prefix} other week from ${data.repeatValue}`;
      break;
    case FREQUENCY_TYPE.MONTH:
    case FREQUENCY_TYPE.MONTHLY:
      resultText =
        +data.repeatInterval === 1
          ? `${prefix} every month from ${data.repeatValue}`
          : `${prefix} other month from ${data.repeatValue}`;
      break;
    case FREQUENCY_TYPE.YEAR:
    case FREQUENCY_TYPE.YEARLY:
      resultText = `${prefix} every year from ${data.repeatValue}`;
      break;
    default:
      break;
  }
  return data.end ? `${resultText} to ${data.end}` : resultText;
}

export function getStandardTime(input) {
  return input < 10 ? `0${input}` : `${input}`;
}

// 2023-09-01 12:00
export function getDayOfMonth(value) {
  return moment(value).date();
}

export function getDayMonthOfYear(value) {
  return moment(value).format("MMMM DD");
}

export function getDayWeekMonthOfYear(value) {
  const date = moment(value);
  const month = date.format("MMMM");
  const dayOfWeek = date.format("dddd");
  const weekOfMonth = Math.ceil(date.date() / 7);
  switch (weekOfMonth) {
    case 1:
      return `first ${dayOfWeek} of ${month}`;
    case 2:
      return `second ${dayOfWeek} of ${month}`;
    case 3:
      return `third ${dayOfWeek} of ${month}`;
    case 4:
      return `fourth ${dayOfWeek} of ${month}`;
    default:
      return `last ${dayOfWeek} of ${month}`;
  }
}

export function getDayWeekOfMonth(value) {
  const date = moment(value);
  const dayOfWeek = date.format("dddd");
  const weekOfMonth = Math.ceil(date.date() / 7);
  switch (weekOfMonth) {
    case 1:
      return `first ${dayOfWeek}`;
    case 2:
      return `second ${dayOfWeek}`;
    case 3:
      return `third ${dayOfWeek}`;
    case 4:
      return `fourth ${dayOfWeek}`;
    default:
      return `last ${dayOfWeek}`;
  }
}

export function transformType(type) {
  switch (type) {
    case FREQUENCY_TYPE.DAY:
      return FREQUENCY_TYPE.DAILY;
    case FREQUENCY_TYPE.WEEK:
      return FREQUENCY_TYPE.WEEKLY;
    case FREQUENCY_TYPE.MONTH:
      return FREQUENCY_TYPE.MONTHLY;
    case FREQUENCY_TYPE.YEAR:
      return FREQUENCY_TYPE.YEARLY;
    case FREQUENCY_TYPE.DAILY:
      return FREQUENCY_TYPE.DAY;
    case FREQUENCY_TYPE.MONTHLY:
      return FREQUENCY_TYPE.MONTH;
    case FREQUENCY_TYPE.WEEKLY:
      return FREQUENCY_TYPE.WEEK;
    case FREQUENCY_TYPE.YEARLY:
      return FREQUENCY_TYPE.YEAR;
    default:
      return REPEAT.NONE;
  }
}

// params: cbGroup = ["Mo", "Tu"]
// result: [1, 2] // Su = 0, Mo = 1
// transform data from UI to api
export function transformWeek(cbGroup) {
  return cbGroup.map((elm) => {
    switch (elm) {
      case "Mo":
        return 1;
      case "Tu":
        return 2;
      case "We":
        return 3;
      case "Th":
        return 4;
      case "Fr":
        return 5;
      case "Sa":
        return 6;
      case "Su":
        return 0;
      case 0:
        return "Su";
      case 1:
        return "Mo";
      case 2:
        return "Tu";
      case 3:
        return "We";
      case 4:
        return "Th";
      case 5:
        return "Fr";
      case 6:
        return "Sa";
      default:
        return null;
    }
  });
}

export function addQueryToURL(url, query) {
  // Kiểm tra xem URL đã có truy vấn chưa
  const hasQuery = url.includes("?");

  // Xử lý truy vấn
  const queryString = Object.keys(query)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
    .join("&");

  // Xây dựng URL mới
  if (hasQuery) {
    return `${url}&${queryString}`;
  } else {
    return `${url}?${queryString}`;
  }
}

export function isJSON(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}
// params: cbGroup = [1,2]
// result: ["Mo", "Tu"]
// transform data from api to StateUI

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

// URL http(s) đầy đủ có domain (cùng quy tắc backend helpers/redirectUrl.js): chặn "abc/xyz", "javascript:..."
export const isHttpUrl = (value) => {
  if (typeof value !== "string" || !value.trim() || /\s/.test(value.trim())) return false;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
    return (
      url.hostname === "localhost" ||
      /\.[a-z0-9-]{2,}$/i.test(url.hostname) ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(url.hostname)
    );
  } catch (error) {
    return false;
  }
};

export const findNestedObjectWithParents = (array, key, value, parents = []) => {
  for (const obj of array) {
    if (obj[key] === value) {
      return [...parents, obj];
    }
    for (const prop in obj) {
      if (Array.isArray(obj[prop])) {
        const result = findNestedObjectWithParents(obj[prop], key, value, [...parents, obj]);
        if (result) {
          return result;
        }
      }
    }
  }
  return null;
};

export const convertTableDataToLocaleNumber = (array) => {
  return array.map((item) => {
    return mapValues(item, (value, key) => {
      if (isNumber(value))
        return parseFloat(value.toFixed(2)).toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
      else if (key === "children" && isArray(value) && value.length) return convertTableDataToLocaleNumber(value);
      else return value;
    });
  });
};

export const splitStringRandomly = (string, min = 3, max = 10) => {
  const result = [];
  let i = 0;
  while (i < string.length) {
    const length = Math.floor(Math.random() * (max - min + 1)) + min;
    const substring = string.slice(i, i + length);
    result.push(substring);
    i += length;
  }
  return result;
};

export const delay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const formatNumberClearly = (number, groupSize = 3, separator = " ") => {
  const regex = new RegExp(`\\B(?=(\\d{${groupSize}})+(?!\\d))`, "g");
  return number.toString().replace(regex, separator);
};

export const clearOnLogout = () => {
  delLocalStorage(LOCAL_STORAGE_TYPE.USER_TOKEN);
  delLocalStorage(LOCAL_STORAGE_TYPE.USER_REFRESH_TOKEN);
  delLocalStorage(LOCAL_STORAGE_TYPE.USER_FULL_NAME);
  delLocalStorage(LOCAL_STORAGE_TYPE.PARTNER_CODE);
  delLocalStorage(LOCAL_STORAGE_TYPE.DATE_ONLY_FORMAT);
  delLocalStorage(LOCAL_STORAGE_TYPE.DATE_TIME_FORMAT);
  delLocalStorage(LOCAL_STORAGE_TYPE.REMEMBER);
  clearAsyncDataCache();
};

export const callViaRef = (getRef, callback) => {
  const current = getRef();
  if (current) {
    callback(current);
  } else {
    setTimeout(() => callViaRef(getRef, callback), 10);
  }
};

export const getParamsFilter = (data) => {
  const obj = {
    ...data,
  };
  Object.keys(obj).forEach((key) => {
    if (obj[key] === null || obj[key] === undefined) {
      obj[key] = "";
    }
  });
  const params = new URLSearchParams(obj).toString();
  return params;
};

export const onClickMenu = ({ domEvent }) => {
  domEvent.stopPropagation();
  domEvent.target.querySelector(".button-in-menu-dropdown")?.click();
};
